import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { useAuth } from '../contexts/AuthContext';
import type { UserPreferences, UpdatePreferencesRequest } from '@sprintpulse/shared';

const defaults: UserPreferences = { userId: '', pinnedProductIds: [], theme: 'system' };

export function usePreferences() {
  const { isLoggedIn } = useAuth();
  const queryClient = useQueryClient();

  const { data: preferences, isLoading } = useQuery({
    queryKey: queryKeys.preferences(),
    queryFn: api.getPreferences,
    enabled: isLoggedIn,
    staleTime: Infinity,
  });

  const mutation = useMutation({
    mutationFn: (req: UpdatePreferencesRequest) => api.updatePreferences(req),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.preferences(), data);
    },
  });

  const togglePinnedProduct = useCallback((productId: string) => {
    const current = preferences?.pinnedProductIds ?? [];
    const next = current.includes(productId)
      ? current.filter((id) => id !== productId)
      : [...current, productId];
    mutation.mutate({ pinnedProductIds: next });
  }, [preferences?.pinnedProductIds, mutation]);

  return {
    preferences: preferences ?? defaults,
    isLoading,
    updatePreferences: mutation.mutate,
    togglePinnedProduct,
  };
}
